import React, { useState, useEffect } from 'react';
import { X, Calendar, User, MapPin, Shield, UserPlus, UserMinus, LogIn, LogOut, Mail, AlertCircle, Clock } from 'lucide-react';
import { auditLogService, AuditLogEntry, AuditAction } from '@/services/auditLogService';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { locationService, Location } from '@/firebase/locationService';
import { userService } from '@/firebase/database';
import { Timestamp } from 'firebase/firestore';

interface UserAuditTrailProps {
  userId: string;
  userName: string;
  userEmail: string;
  onClose: () => void;
}

const UserAuditTrail: React.FC<UserAuditTrailProps> = ({ userId, userName, userEmail, onClose }) => {
  const { user: currentUser } = useAuth();
  const { currentOrganization } = useOrganization();
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [locations, setLocations] = useState<Record<string, Location>>({});
  const [performedByUsers, setPerformedByUsers] = useState<Record<string, { name: string; email: string }>>({});
  const [userStats, setUserStats] = useState<{
    createdBy?: { name: string; email: string; date: Date };
    lastLogin?: Date;
    totalLogins: number;
    locationChanges: number;
    roleChanges: number;
  }>({
    totalLogins: 0,
    locationChanges: 0,
    roleChanges: 0,
  });

  useEffect(() => {
    loadAuditLogs();
    loadLocations();
  }, [userId, currentOrganization?.id]);

  const loadLocations = async () => {
    if (!currentOrganization?.id) return;
    
    try {
      const result = await locationService.getAllLocations(currentOrganization.id);
      if (result.success && result.data) {
        const locationsMap: Record<string, Location> = {};
        result.data.forEach((loc: Location) => {
          if (loc.id) {
            locationsMap[loc.id] = loc;
          }
        });
        setLocations(locationsMap);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadAuditLogs = async () => {
    setIsLoading(true);
    try {
      console.log('📋 Loading audit logs for user:', {
        userId,
        organizationId: currentOrganization?.id,
      });
      
      const result = await auditLogService.getUserAuditLogs(userId, currentOrganization?.id, 200);
      
      console.log('📋 Audit logs result:', {
        success: result.success,
        count: result.data?.length || 0,
        error: result.error,
        loginLogs: result.data?.filter(log => log.action === 'user_login').length || 0,
      });
      
      if (result.success && result.data) {
        setAuditLogs(result.data);
        
        // Calculate stats
        const stats = {
          totalLogins: 0,
          locationChanges: 0,
          roleChanges: 0,
        };
        
        let createdBy: { name: string; email: string; date: Date } | undefined;
        let lastLogin: Date | undefined;
        
        result.data.forEach((log) => {
          if (log.action === 'user_login') {
            stats.totalLogins++;
            let timestampDate: Date;
            if (log.timestamp instanceof Date) {
              timestampDate = log.timestamp;
            } else if (log.timestamp && typeof log.timestamp === 'object' && 'toDate' in log.timestamp) {
              timestampDate = (log.timestamp as Timestamp).toDate();
            } else {
              timestampDate = new Date(log.timestamp as string | number);
            }
            
            if (!lastLogin || timestampDate > lastLogin) {
              lastLogin = timestampDate;
            }
          }
          if (log.action === 'location_assigned' || log.action === 'location_removed') {
            stats.locationChanges++;
          }
          if (log.action === 'role_changed' || log.action === 'permission_changed') {
            stats.roleChanges++;
          }
          if (log.action === 'user_created' || log.action === 'invitation_accepted') {
            if (!createdBy) {
              let timestampDate: Date;
              if (log.timestamp instanceof Date) {
                timestampDate = log.timestamp;
              } else if (log.timestamp && typeof log.timestamp === 'object' && 'toDate' in log.timestamp) {
                timestampDate = (log.timestamp as Timestamp).toDate();
              } else {
                timestampDate = new Date(log.timestamp as string | number);
              }
              
              createdBy = {
                name: log.performedByName || 'Unknown',
                email: log.performedByEmail || 'Unknown',
                date: timestampDate,
              };
            }
          }
        });
        
        setUserStats({
          ...stats,
          createdBy,
          lastLogin,
        });
        
        // Load user names for performedBy
        const uniqueUserIds = new Set<string>();
        result.data.forEach(log => {
          if (log.performedBy) {
            uniqueUserIds.add(log.performedBy);
          }
        });
        
        const userMap: Record<string, { name: string; email: string }> = {};
        await Promise.all(
          Array.from(uniqueUserIds).map(async (uid) => {
            try {
              const userResult = await userService.getUser(uid);
              if (userResult.success && userResult.data) {
                userMap[uid] = {
                  name: userResult.data.name || 'Unknown',
                  email: userResult.data.email || 'Unknown',
                };
              }
            } catch (error) {
              console.error(`Error loading user ${uid}:`, error);
            }
          })
        );
        
        setPerformedByUsers(userMap);
      }
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getActionIcon = (action: AuditAction) => {
    switch (action) {
      case 'user_created':
      case 'invitation_accepted':
        return <UserPlus className="w-4 h-4" />;
      case 'user_removed':
        return <UserMinus className="w-4 h-4" />;
      case 'location_assigned':
      case 'location_removed':
        return <MapPin className="w-4 h-4" />;
      case 'role_changed':
      case 'permission_changed':
        return <Shield className="w-4 h-4" />;
      case 'user_login':
        return <LogIn className="w-4 h-4" />;
      case 'user_logout':
        return <LogOut className="w-4 h-4" />;
      case 'invitation_sent':
        return <Mail className="w-4 h-4" />;
      case 'invitation_cancelled':
        return <X className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getActionLabel = (action: AuditAction): string => {
    switch (action) {
      case 'user_created':
        return 'User Created';
      case 'user_updated':
        return 'User Updated';
      case 'user_removed':
        return 'User Removed';
      case 'location_assigned':
        return 'Location Assigned';
      case 'location_removed':
        return 'Location Removed';
      case 'role_changed':
        return 'Role Changed';
      case 'permission_changed':
        return 'Permission Changed';
      case 'user_login':
        return 'User Login';
      case 'user_logout':
        return 'User Logout';
      case 'invitation_sent':
        return 'Invitation Sent';
      case 'invitation_accepted':
        return 'Invitation Accepted';
      case 'invitation_cancelled':
        return 'Invitation Cancelled';
      default:
        return action;
    }
  };

  const formatDate = (date: Date | Timestamp | string | number): string => {
    let d: Date;
    if (date instanceof Date) {
      d = date;
    } else if (date && typeof date === 'object' && 'toDate' in date) {
      d = (date as Timestamp).toDate();
    } else {
      d = new Date(date as string | number);
    }
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatChange = (change: { field: string; oldValue: any; newValue: any }): string => {
    if (change.field === 'locationIds') {
      const oldLocs = Array.isArray(change.oldValue) ? change.oldValue : [];
      const newLocs = Array.isArray(change.newValue) ? change.newValue : [];
      const added = newLocs.filter((id: string) => !oldLocs.includes(id));
      const removed = oldLocs.filter((id: string) => !newLocs.includes(id));
      
      const parts: string[] = [];
      if (added.length > 0) {
        const addedNames = added.map((id: string) => locations[id]?.name || id).join(', ');
        parts.push(`Added: ${addedNames}`);
      }
      if (removed.length > 0) {
        const removedNames = removed.map((id: string) => locations[id]?.name || id).join(', ');
        parts.push(`Removed: ${removedNames}`);
      }
      return parts.join(' | ') || 'No changes';
    }
    
    if (change.field === 'role' || change.field === 'roles') {
      return `${JSON.stringify(change.oldValue)} → ${JSON.stringify(change.newValue)}`;
    }
    
    return `${change.oldValue || 'N/A'} → ${change.newValue || 'N/A'}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">User Audit Trail</h2>
            <p className="text-sm text-gray-500 mt-1">{userName} ({userEmail})</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Stats Section */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-gray-500 mb-1">Created By</div>
              <div className="text-sm font-medium text-gray-900">
                {userStats.createdBy ? (
                  <>
                    {userStats.createdBy.name}
                    <div className="text-xs text-gray-500 mt-0.5">
                      {formatDate(userStats.createdBy.date)}
                    </div>
                  </>
                ) : (
                  'Unknown'
                )}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Last Login</div>
              <div className="text-sm font-medium text-gray-900">
                {userStats.lastLogin ? (
                  <>
                    {formatDate(userStats.lastLogin)}
                  </>
                ) : (
                  'Never'
                )}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Total Logins</div>
              <div className="text-sm font-medium text-gray-900">{userStats.totalLogins}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Location Changes</div>
              <div className="text-sm font-medium text-gray-900">{userStats.locationChanges}</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading audit trail...</div>
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500 text-center">
                <p className="font-medium">No audit logs found</p>
                <p className="text-sm mt-1">No activity has been recorded for this user yet.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {auditLogs.map((log) => {
                const performedBy = (log.performedBy && performedByUsers[log.performedBy]) || {
                  name: log.performedByName || 'Unknown',
                  email: log.performedByEmail || 'Unknown',
                };

                return (
                  <div
                    key={log.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600">
                          {getActionIcon(log.action)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900">
                                {getActionLabel(log.action)}
                              </span>
                              {log.performedBy === currentUser?.id && (
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                  You
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                              Performed by: <span className="font-medium">{performedBy.name}</span> ({performedBy.email})
                            </div>
                            {log.changes && log.changes.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {log.changes.map((change, idx) => (
                                  <div key={idx} className="text-sm bg-gray-50 p-2 rounded border border-gray-200">
                                    <span className="font-medium text-gray-700">{change.field}:</span>{' '}
                                    <span className="text-gray-600">{formatChange(change)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {log.metadata && Object.keys(log.metadata).length > 0 && (
                              <div className="mt-2 text-xs text-gray-500">
                                {Object.entries(log.metadata).map(([key, value]) => (
                                  <div key={key}>
                                    <span className="font-medium">{key}:</span> {String(value)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex-shrink-0 text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(log.timestamp)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {auditLogs.length} audit log{auditLogs.length !== 1 ? 's' : ''}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAuditTrail;

